<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { getArticle, getArticles, type Article } from '../api';
import closeIcon from '../assets/close.svg';
import casesHero from '../assets/page-heroes/cases.jpg';
import newsHero from '../assets/page-heroes/news.jpg';
import { formatDate, locale, text } from '../i18n';

const route = useRoute();
const router = useRouter();
const type = computed(() => route.meta.type as 'news' | 'case');
const articles = ref<Article[]>([]);
const loading = ref(true);
const error = ref('');
const selectedSlug = computed(() => typeof route.query.article === 'string' ? route.query.article : '');
const selectedArticle = ref<Article | null>(null);
const detailLoading = ref(false);
const detailError = ref('');
const detailImageOrientation = ref<'default' | 'portrait'>('default');
let detailRequest = 0;
let listScrollY: number | null = null;

const listPath = computed(() => type.value === 'case' ? '/cases' : '/news');
const listRoute = computed(() => {
  const query = { ...route.query };
  delete query.article;
  return { path: listPath.value, query };
});

function articleLink(slug: string) {
  return { path: listPath.value, query: { ...route.query, article: slug } };
}

function closeDetail() {
  void router.replace(listRoute.value);
}

function detectDetailImageOrientation(event: Event) {
  const image = event.currentTarget as HTMLImageElement | null;
  if (!image?.naturalWidth || !image.naturalHeight) return;

  // Only treat clearly portrait images as a special layout. Near-square and
  // landscape images keep the established article reading flow.
  detailImageOrientation.value = image.naturalHeight / image.naturalWidth >= 1.25 ? 'portrait' : 'default';
}

function rememberListScrollPosition() {
  if (listScrollY === null) listScrollY = window.scrollY;
}

function restoreListScrollPosition() {
  if (listScrollY === null) return;

  const position = listScrollY;
  const restore = () => {
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo({ top: position, left: 0, behavior: 'auto' });
    root.style.scrollBehavior = previousBehavior;
  };

  void nextTick(() => {
    requestAnimationFrame(() => {
      restore();
      window.setTimeout(restore, 0);
    });
  });
}

watch([type, locale], async () => {
  loading.value = true;
  try {
    articles.value = (await getArticles(locale.value, type.value)).data;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : text('内容加载失败', 'Unable to load content');
  } finally {
    loading.value = false;
  }
}, { immediate: true });

watch([selectedSlug, type, locale], async () => {
  const slug = selectedSlug.value;
  if (slug && listScrollY === null) rememberListScrollPosition();
  document.body.classList.toggle('article-reader-open', Boolean(slug));
  restoreListScrollPosition();
  if (!slug) {
    selectedArticle.value = null;
    detailError.value = '';
    detailImageOrientation.value = 'default';
    listScrollY = null;
    return;
  }

  const requestId = ++detailRequest;
  detailLoading.value = true;
  detailError.value = '';
  detailImageOrientation.value = 'default';
  selectedArticle.value = null;
  try {
    const result = await getArticle(locale.value, type.value, slug);
    if (requestId === detailRequest) selectedArticle.value = result;
  } catch (reason) {
    if (requestId === detailRequest) detailError.value = reason instanceof Error ? reason.message : text('内容加载失败', 'Unable to load content');
  } finally {
    if (requestId === detailRequest) detailLoading.value = false;
  }
}, { immediate: true });

onBeforeUnmount(() => document.body.classList.remove('article-reader-open'));
</script>

<template>
  <div class="legacy-pages">
    <section
      class="portal-page-hero"
      :class="type === 'case' ? 'portal-page-hero--cases' : 'portal-page-hero--news'"
    >
      <img
        :src="type === 'case' ? casesHero : newsHero"
        :alt="type === 'case' ? text('杭州技卫士人体安检项目现场', 'JWS body screening deployment') : text('杭州技卫士公司与技术动态', 'JWS company and technology updates')"
        decoding="async"
        fetchpriority="high"
      >
      <div class="container">
        <span class="portal-eyebrow">{{ type === 'case' ? text('行业案例', 'Projects') : text('新闻资讯', 'News') }}</span>
        <h1>{{ type === 'case' ? text('以真实项目验证产品与交付能力', 'Products and delivery proven in real projects') : text('记录研发、交流与项目进展', 'Engineering, collaboration and project progress') }}</h1>
        <p>{{ type === 'case' ? text('围绕司法监所、机场交通、矿山企业和重点设施，积累不同运行条件下的部署经验。', 'Deployment experience across corrections, transport, mining and critical facilities.') : text('持续发布公司动态、技术交流和人体安检系统的项目交付信息。', 'Company updates, technical exchanges and people-screening project deliveries.') }}</p>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <header class="section-heading section-heading--wide">
          <span class="eyebrow">{{ type === 'case' ? text('项目实践', 'Deployments') : text('最新动态', 'Latest updates') }}</span><h2>{{ type === 'case' ? text('不同场景，同一套可靠交付标准', 'One delivery standard across different environments') : text('来自研发与项目一线的真实记录', 'Updates from engineering and project teams') }}</h2>
        </header>
        <div
          v-if="articles.length"
          class="article-list"
        >
          <article
            v-for="(article, index) in articles"
            :key="article.id"
          >
            <RouterLink
              class="article-list__media"
              :to="articleLink(article.slug)"
              :aria-label="`${text('阅读', 'Read ')} ${article.title}`"
              @pointerdown="rememberListScrollPosition"
              @keydown.enter="rememberListScrollPosition"
              @click="rememberListScrollPosition"
            >
              <img
                v-if="article.coverUrl"
                :src="article.coverUrl"
                :alt="article.title"
                loading="lazy"
              ><div
                v-else
                class="media-placeholder"
              >
                JWS
              </div><span>{{ String(index + 1).padStart(2, '0') }}</span>
            </RouterLink><div>
              <time>{{ formatDate(article.publishedAt) }}</time><h2>
                <RouterLink
                  :to="articleLink(article.slug)"
                  @pointerdown="rememberListScrollPosition"
                  @keydown.enter="rememberListScrollPosition"
                  @click="rememberListScrollPosition"
                >
                  {{ article.title }}
                </RouterLink>
              </h2><p>{{ article.excerpt }}</p><RouterLink
                class="text-link"
                :to="articleLink(article.slug)"
                @pointerdown="rememberListScrollPosition"
                @keydown.enter="rememberListScrollPosition"
                @click="rememberListScrollPosition"
              >
                {{ type === 'case' ? text('查看案例', 'View project') : text('阅读全文', 'Read article') }} <b>→</b>
              </RouterLink>
            </div>
          </article>
        </div>
        <p
          v-else
          class="empty-state"
        >
          {{ loading ? text('正在加载内容…', 'Loading content…') : error || text('当前暂无已发布内容。', 'No published content is available.') }}
        </p>
      </div>
    </section>
    <Teleport to="body">
      <section
        v-if="selectedSlug"
        class="article-reader-layer"
        role="dialog"
        aria-modal="true"
        :aria-label="type === 'case' ? text('案例详情', 'Project details') : text('新闻详情', 'News details')"
        @click.self="closeDetail"
      >
        <div class="article-reader-panel">
          <RouterLink
            class="article-reader-panel__close"
            :to="listRoute"
            :aria-label="text('关闭详情', 'Close details')"
          >
            <img class="article-reader-panel__close-icon" :src="closeIcon" alt="" />
          </RouterLink>
          <div
            v-if="detailLoading"
            class="article-reader-panel__state"
          >
            {{ text('正在加载内容…', 'Loading content…') }}
          </div>
          <div
            v-else-if="detailError"
            class="article-reader-panel__state is-error"
          >
            <p>{{ detailError }}</p><RouterLink class="text-link" :to="listRoute">{{ text('返回新闻列表', 'Back to list') }} <b>→</b></RouterLink>
          </div>
          <article
            v-else-if="selectedArticle"
            class="article-reader-panel__content"
            :class="{ 'article-reader-panel__content--portrait': detailImageOrientation === 'portrait' }"
          >
            <div class="article-reader-panel__copy">
              <time>{{ formatDate(selectedArticle.publishedAt) }}</time>
              <h2>{{ selectedArticle.title }}</h2>
              <p
                v-if="selectedArticle.excerpt"
                class="article-reader-panel__excerpt"
              >
                {{ selectedArticle.excerpt }}
              </p>
            </div>
            <figure v-if="selectedArticle.coverUrl">
              <img
                :src="selectedArticle.coverUrl"
                :alt="selectedArticle.title"
                @load="detectDetailImageOrientation"
              >
            </figure>
            <div class="article-reader-panel__body">
              <div
                v-if="selectedArticle.contentHtml"
                class="rich-content"
                v-html="selectedArticle.contentHtml"
              />
              <p v-else>{{ selectedArticle.excerpt }}</p>
            </div>
          </article>
        </div>
      </section>
    </Teleport>
  </div>
</template>
